import { useState, useEffect, useRef } from 'react';
import { getBlockedTimes, createBlockedTime, updateBlockedTime, deleteBlockedTime, getReservations, getServices, getSettings } from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import { mapBlockedTimesToEvents, getServiceColor } from '../utils/calendar';
import AdminPageSkeleton from '../components/AdminPageSkeleton';
import ConfirmModal from '../components/ConfirmModal';
import Error500 from './errors/Error500';
import FullCalendar from '@fullcalendar/react';
import { formatPhoneNumber } from '../utils/formatters';
import './BlockedTimesModal.css';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import skLocale from '@fullcalendar/core/locales/sk';
import './AdminPages.css';
import '../components/ReservationCalendar.css';

const AdminBlockedTimesPage = () => {
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingBlocked, setEditingBlocked] = useState(null);
  const [modalError, setModalError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [setCurrentView] = useState(window.innerWidth <= 768 ? 'timeGridDay' : 'timeGridWeek');
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);
  
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  
  const [calendarSettings, setCalendarSettings] = useState({
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00'
  });
  
  const calendarRef = useRef(null);

  const [formData, setFormData] = useState({
    nazov: '',
    popis: '',
    typ_blokovania: 'casovy_rozsah',
    zaciatok: '',
    koniec: '',
    zaciatok_cas: '09:00',
    koniec_cas: '17:00',
    opakovat_typ: '',
    opakovat_od: '',
    opakovat_do: '',
    selectedDays: [],
    cely_den_opakovanie: false,
    donekonecna: false
  });

  const daysOfWeek = [
    { code: 'PO', label: 'Pondelok', short: 'PO', accusative: 'pondelok', gender: 'm' },
    { code: 'UT', label: 'Utorok', short: 'UT', accusative: 'utorok', gender: 'm' },
    { code: 'ST', label: 'Streda', short: 'ST', accusative: 'stredu', gender: 'f' },
    { code: 'STV', label: 'Štvrtok', short: 'ŠT', accusative: 'štvrtok', gender: 'm' },
    { code: 'PI', label: 'Piatok', short: 'PI', accusative: 'piatok', gender: 'm' },
    { code: 'SO', label: 'Sobota', short: 'SO', accusative: 'sobotu', gender: 'f' },
    { code: 'NE', label: 'Nedeľa', short: 'NE', accusative: 'nedeľu', gender: 'f' }
  ];

  const toggleDay = (dayCode) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays || [];
      return {
        ...prev,
        selectedDays: selectedDays.includes(dayCode)
          ? selectedDays.filter(d => d !== dayCode)
          : [...selectedDays, dayCode]
      };
    });
  };

  const getRecurrenceSummary = () => {
    if (formData.typ_blokovania !== 'opakujuce_sa') return '';
    
    const selectedDays = formData.selectedDays || [];
    if (selectedDays.length === 0) return '';
    
    const workDays = ['PO', 'UT', 'ST', 'STV', 'PI'];
    const weekend = ['SO', 'NE'];
    
    const isWorkWeek = workDays.every(day => selectedDays.includes(day)) && selectedDays.length === 5;
    const isWeekend = weekend.every(day => selectedDays.includes(day)) && selectedDays.length === 2;
    
    let daysText;
    
    if (selectedDays.length === 7) {
      daysText = 'každý deň';
    } else if (isWorkWeek) {
      daysText = 'každý pracovný deň';
    } else if (isWeekend) {
      daysText = 'každý víkend';
    } else {
      const dayObjects = selectedDays.map(code => 
        daysOfWeek.find(d => d.code === code)
      ).filter(Boolean);
      
      if (dayObjects.length === 1) {
        const day = dayObjects[0];
        const kazdy = day.gender === 'f' ? 'každú' : 'každý';
        daysText = `${kazdy} ${day.accusative}`;
      } else {
        const daysWithKazdy = dayObjects.map(d => {
          const kazdy = d.gender === 'f' ? 'každú' : 'každý';
          return `${kazdy} ${d.accusative}`;
        });
        
        daysText = daysWithKazdy.slice(0, -1).join(', ') + ' a ' + daysWithKazdy[daysWithKazdy.length - 1];
      }
    }
    
    return `Blokovanie sa bude opakovať ${daysText} od ${formData.zaciatok_cas} do ${formData.koniec_cas}.`;
  };

  useEffect(() => {
    loadSettings();
    loadData();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      const data = response.data.data || response.data;
      const settings = data.settings || {};
      
      let maxTime = settings.calendar_slot_max_time || '20:00';
      const [maxHour, maxMin] = maxTime.split(':').map(Number);
      const adjustedMaxHour = maxHour + 1;
      const adjustedMaxTime = `${String(adjustedMaxHour).padStart(2, '0')}:${String(maxMin).padStart(2, '0')}:00`;
      
      setCalendarSettings({
        slotMinTime: settings.calendar_slot_min_time ? `${settings.calendar_slot_min_time}:00` : '07:00:00',
        slotMaxTime: adjustedMaxTime
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile && calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const currentViewType = calendarApi.view.type;
        
        if (currentViewType === 'timeGridWeek') {
          calendarApi.changeView('timeGridDay');
          setCurrentView('timeGridDay');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [setCurrentView, setIsMobile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [blockedResponse, reservationsResponse, servicesResponse] = await Promise.all([
        getBlockedTimes(),
        getReservations(),
        getServices()
      ]);
      
      setBlockedTimes(blockedResponse.data.data || []);
      setReservations(reservationsResponse.data.data || []);
      setServices(servicesResponse.data.data || []);
      setError(null);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load data:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      setError('Nepodarilo sa načítať dáta. Skúste to prosím neskôr.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (info) => {
    const clickedDate = info.dateStr.split('T')[0];
    setFormData({
      nazov: 'Blokované',
      popis: '',
      typ_blokovania: 'cely_den',
      zaciatok: clickedDate,
      koniec: clickedDate,
      zaciatok_cas: '09:00',
      koniec_cas: '17:00',
      opakovat_typ: '',
      opakovat_od: '',
      opakovat_do: ''
    });
    setModalMode('add');
    setEditingBlocked(null);
    setModalError('');
    setFieldErrors({});
    setShowModal(true);
  };

  const handleDateSelect = (selectInfo) => {
    const startDate = selectInfo.start;
    const endDate = selectInfo.end;
    
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = new Date(endDate.getTime() - 1).toISOString().split('T')[0];
    
    const startTime = startDate.toTimeString().slice(0, 5);
    const endTime = endDate.toTimeString().slice(0, 5);
    
    if (startDateStr !== endDateStr) {
      setFormData({
        nazov: 'Blokované',
        popis: '',
        typ_blokovania: 'viacero_dni',
        zaciatok: startDateStr,
        koniec: endDateStr,
        zaciatok_cas: '09:00',
        koniec_cas: '17:00',
        opakovat_typ: '',
        opakovat_od: '',
        opakovat_do: ''
      });
    } else {
      setFormData({
        nazov: 'Blokované',
        popis: '',
        typ_blokovania: 'casovy_rozsah',
        zaciatok: startDateStr,
        koniec: startDateStr,
        zaciatok_cas: startTime,
        koniec_cas: endTime,
        opakovat_typ: '',
        opakovat_od: '',
        opakovat_do: ''
      });
    }
    
    setModalMode('add');
    setEditingBlocked(null);
    setModalError('');
    setFieldErrors({});
    setShowModal(true);
    
    selectInfo.view.calendar.unselect();
  };

  const handleAddNew = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      nazov: '',
      popis: '',
      typ_blokovania: 'casovy_rozsah',
      zaciatok: today,
      koniec: today,
      zaciatok_cas: '09:00',
      koniec_cas: '17:00',
      opakovat_typ: '',
      opakovat_od: '',
      opakovat_do: ''
    });
    setModalMode('add');
    setEditingBlocked(null);
    setModalError('');
    setFieldErrors({});
    setShowModal(true);
  };

  const handleEditBlocked = (blocked) => {
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split(' ')[0].split('-');
      return `${parts[0]}-${parts[1]}-${parts[2]}`;
    };
    
    const parseTime = (dateStr) => {
      if (!dateStr) return '09:00';
      const timePart = dateStr.split(' ')[1];
      if (!timePart) return '09:00';
      return timePart.substring(0, 5);
    };
    
    let zaciatokDate = parseDate(blocked.zaciatok);
    let koniecDate = parseDate(blocked.koniec);
    
    if (blocked.typ_blokovania === 'viacero_dni' && koniecDate) {
      const koniecParts = koniecDate.split('-');
      const d = new Date(parseInt(koniecParts[0]), parseInt(koniecParts[1]) - 1, parseInt(koniecParts[2]));
      d.setDate(d.getDate() - 1);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      koniecDate = `${year}-${month}-${day}`;
    }

    setFormData({
      nazov: blocked.nazov || '',
      popis: blocked.popis || '',
      typ_blokovania: blocked.typ_blokovania || 'casovy_rozsah',
      zaciatok: zaciatokDate || '',
      koniec: koniecDate || '',
      zaciatok_cas: parseTime(blocked.zaciatok),
      koniec_cas: parseTime(blocked.koniec),
      opakovat_typ: blocked.opakovat_typ || '',
      opakovat_od: blocked.opakovat_od || '',
      opakovat_do: blocked.opakovat_do || ''
    });
    setModalMode('edit');
    setEditingBlocked(blocked);
    setModalError('');
    setFieldErrors({});
    setShowModal(true);
  };

  const handleDeleteBlocked = (blocked) => {
    setConfirmModalTitle('Zmazať blokovanie');
    setConfirmModalMessage(`Naozaj chcete vymazať blokovanie "${blocked.nazov}"?`);
    setConfirmModalAction(() => async () => {
      try {
        await deleteBlockedTime(blocked.id);
        setBlockedTimes(prev => prev.filter(b => b.id !== blocked.id));
      } catch (err) {
        console.error('Failed to delete:', err);
        setNotificationType('error');
        setNotificationMessage('Nepodarilo sa vymazať blokovanie');
        setShowNotificationModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setFieldErrors({});

    try {
      const payload = {
        nazov: formData.nazov,
        popis: formData.popis,
        typ_blokovania: formData.typ_blokovania
      };

      if (formData.typ_blokovania === 'cely_den') {
        payload.zaciatok = `${formData.zaciatok} 00:00:00`;
        payload.koniec = `${formData.zaciatok} 23:59:59`;
      } else if (formData.typ_blokovania === 'viacero_dni') {
        payload.zaciatok = `${formData.zaciatok} 00:00:00`;
        const endDate = new Date(formData.koniec);
        endDate.setDate(endDate.getDate() + 1);
        const endDateStr = endDate.toISOString().split('T')[0];
        payload.koniec = `${endDateStr} 00:00:00`;
      } else if (formData.typ_blokovania === 'casovy_rozsah') {
        payload.zaciatok = `${formData.zaciatok} ${formData.zaciatok_cas}:00`;
        payload.koniec = `${formData.zaciatok} ${formData.koniec_cas}:00`;
      } else if (formData.typ_blokovania === 'opakujuce_sa') {
        const zaciatokDatum = formData.opakovat_od || formData.zaciatok || new Date().toISOString().split('T')[0];
        
        const zaciatokCas = formData.cely_den_opakovanie ? '00:00' : formData.zaciatok_cas;
        const koniecCas = formData.cely_den_opakovanie ? '23:59' : formData.koniec_cas;
        
        payload.zaciatok = `${zaciatokDatum} ${zaciatokCas}:00`;
        payload.koniec = `${zaciatokDatum} ${koniecCas}:00`;
        payload.opakovat_typ = 'tyzdenne';
        
        if (formData.donekonecna) {
          payload.opakovat_do = '2099-12-31';
        } else if (formData.opakovat_do) {
          payload.opakovat_do = formData.opakovat_do;
        }
        
        if (formData.opakovat_od) {
          payload.opakovat_od = formData.opakovat_od;
        }
        
        if (formData.selectedDays && formData.selectedDays.length > 0) {
          payload.vybrane_dni = formData.selectedDays.map(d => d.toLowerCase()).join(',');
        }
      }
      
      console.log('Payload being sent:', payload);

      if (modalMode === 'add') {
        const response = await createBlockedTime(payload);
        const newBlock = response.data.data || response.data;
        setBlockedTimes(prev => [...prev, newBlock]);
        closeModalWithAnimation(() => {
          setShowModal(false);
        });
      } else {
        await updateBlockedTime(editingBlocked.id, payload);
        setBlockedTimes(prev => prev.map(b => 
          b.id === editingBlocked.id 
            ? { ...b, ...payload, nazov: formData.nazov, popis: formData.popis }
            : b
        ));
        closeModalWithAnimation(() => {
          setShowModal(false);
        });
      }
    } catch (err) {
      console.error('Failed to save:', err);
      const errorMessage = err.response?.data?.message || 'Nepodarilo sa uložiť blokovanie';
      setModalError(errorMessage);
      if (err.response?.data?.errors) {
        setFieldErrors(err.response.data.errors);
      }
    }
  };


  const calendarEvents = [
    ...reservations.map(res => {
      const service = services.find(s => s.id === res.sluzba_id);
      const duration = service ? service.trvanie_minuty : 60;
      
      const startDateTime = new Date(`${res.datum_rezervacie}T${res.cas_rezervacie}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
      
      const now = new Date();
      let timeStatus = 'upcoming';
      
      if (now > endDateTime) {
        timeStatus = 'past';
      } else if (now >= startDateTime && now <= endDateTime) {
        timeStatus = 'current';
      }

      const serviceColor = res.nazov_sluzby ? getServiceColor(res.nazov_sluzby) : { bg: '#f3f4f6', text: '#6b7280' };
      
      return {
        id: `res-${res.id}`,
        title: res.nazov_sluzby || 'Neznáma služba',
        start: startDateTime,
        end: endDateTime,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        textColor: '#111827',
        extendedProps: {
          type: 'reservation',
          data: res,
          reservationData: res,
          serviceColorBg: serviceColor.bg,
          serviceColorText: serviceColor.text,
          timeStatus: timeStatus
        },
        classNames: [
          'custom-event',
          res.stav === 'pending' ? 'status-pending' : 
          res.stav === 'confirmed' ? 'status-confirmed' : 
          res.stav === 'cancelled' ? 'status-cancelled' : 'status-confirmed',
          `time-${timeStatus}`
        ]
      };
    }),
    ...mapBlockedTimesToEvents(blockedTimes, {
      slotMinTime: calendarSettings.slotMinTime,
      slotMaxTime: calendarSettings.slotMaxTime
    })
  ];

  const handleEventClick = (info) => {
    const eventType = info.event.extendedProps.type;
    if (eventType === 'blocked') {
      handleEditBlocked(info.event.extendedProps.data);
    }
  };

  const getBlockedTimesEventTextStyle = (eventInfo) => {
    const reservationStatus = eventInfo.event.extendedProps?.reservationData?.stav;
    const timeStatus = eventInfo.event.extendedProps?.timeStatus;

    if (reservationStatus === 'cancelled' && timeStatus === 'past') {
      return { color: '#B45353' };
    }

    return null;
  };

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Blokované časy"
        subtitle="Správa blokovaných časov, dovoleniek a sviatkov"
        variant="calendar"
      />
    );
  }

  if (serverError) {
    return <Error500 />;
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="alert alert-error mb-3">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Blokované časy</h1>
          <p className="text-muted">Správa blokovaných časov, dovoleniek a sviatkov</p>
        </div>
        <div className="header-actions">
          <button onClick={handleAddNew} className="btn btn-primary btn-lg">Pridať blokovanie</button>
        </div>
      </div>

      <div className="calendar-view">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
          locale={skLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: isMobile ? '' : 'timeGridDay,timeGridWeek'
          }}
          buttonText={{
            today: 'Dnes',
            day: 'Deň',
            week: 'Týždeň'
          }}
          titleFormat={() => ''}
          datesSet={(dateInfo) => {
            const titleEl = document.querySelector('.fc-toolbar-title');
            if (!titleEl) return;
            
            if (dateInfo.view.type === 'timeGridDay') {
              const date = dateInfo.view.currentStart;
              const weekday = date.toLocaleDateString('sk-SK', { weekday: 'long' });
              const day = date.getDate();
              const month = date.toLocaleDateString('sk-SK', { month: 'long' });
              const year = date.getFullYear();
              
              titleEl.innerHTML = `<span style="font-weight: 400">${weekday}</span> <strong>${day}. ${month} ${year}</strong>`;
            } else if (dateInfo.view.type === 'timeGridWeek' || dateInfo.view.type === 'listWeek') {
              const startDate = dateInfo.view.currentStart;
              const endDate = new Date(dateInfo.view.currentEnd);
              endDate.setDate(endDate.getDate() - 1);
              
              const startDay = startDate.getDate();
              const startMonth = startDate.toLocaleDateString('sk-SK', { month: 'long' });
              const startYear = startDate.getFullYear();
              const endDay = endDate.getDate();
              const endMonth = endDate.toLocaleDateString('sk-SK', { month: 'long' });
              const endYear = endDate.getFullYear();
              
              if (startYear !== endYear) {
                titleEl.innerHTML = `<strong>${startDay}. ${startMonth} ${startYear} – ${endDay}. ${endMonth} ${endYear}</strong>`;
              } else {
                titleEl.innerHTML = `<strong>${startDay}. ${startMonth} – ${endDay}. ${endMonth} ${endYear}</strong>`;
              }
            }
          }}
          events={calendarEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          select={handleDateSelect}
          eventContent={(eventInfo) => {
            const reservationTextStyle = getBlockedTimesEventTextStyle(eventInfo);

            if (eventInfo.event.extendedProps.isAllDayBlocking) {
              return (
                <div className="fc-event-main-frame">
                  <div className="fc-event-title-container">
                    <div className="fc-event-title fc-sticky" style={reservationTextStyle}>
                      <div style={{ fontSize: '0.85em', marginTop: '1px', opacity: 0.9, marginBottom: '1px', fontWeight: 'normal' }}>
                        Celý deň
                      </div>
                      {eventInfo.event.title}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="fc-event-main-frame">
                <div className="fc-event-time" style={reservationTextStyle}>{eventInfo.timeText}</div>
                <div className="fc-event-title-container">
                  <div className="fc-event-title fc-sticky" style={reservationTextStyle}>
                    {eventInfo.event.title}
                  </div>
                </div>
              </div>
            );
          }}
          height="auto"
          slotMinTime={calendarSettings.slotMinTime}
          slotMaxTime={calendarSettings.slotMaxTime}
          slotDuration="00:30:00"
          slotLabelInterval="01:00"
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          allDaySlot={false}
          nowIndicator={true}
          editable={false}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          slotEventOverlap={false}
          eventMinHeight={50}
          eventShortHeight={50}
          expandRows={true}
          eventMouseEnter={(info) => {
            if (info.event.extendedProps.type === 'blocked') {
              return;
            }
            
            const reservation = info.event.extendedProps.reservationData;
            if (!reservation) return;
            
            const { cele_meno, telefon, adresa } = reservation;
            
            if (info.el._hideTimeout) {
              clearTimeout(info.el._hideTimeout);
              delete info.el._hideTimeout;
            }
            
            if (info.el._tooltip) return;
            
            const tooltip = document.createElement('div');
            tooltip.className = 'calendar-tooltip';
            
            const encodedAddress = encodeURIComponent(adresa || '');
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
            
            tooltip.innerHTML = `
              <div class="tooltip-row"><strong>Zákazník:</strong> ${cele_meno}</div>
              <div class="tooltip-row">
                <strong>Telefón:</strong> 
                <a href="tel:${formatPhoneNumber(telefon)}" class="tooltip-link">${formatPhoneNumber(telefon)}</a>
              </div>
              <div class="tooltip-row">
                <strong>Adresa:</strong> 
                <a href="${mapsUrl}" target="_blank" class="tooltip-link tooltip-maps">
                  ${adresa}
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="margin-left: 4px; vertical-align: middle;">
                    <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
                    <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
                  </svg>
                </a>
              </div>
            `;
            
            tooltip.addEventListener('mouseenter', () => {
              if (info.el._hideTimeout) {
                clearTimeout(info.el._hideTimeout);
                delete info.el._hideTimeout;
              }
            });
            
            tooltip.addEventListener('mouseleave', () => {
              if (info.el._tooltip) {
                info.el._tooltip.remove();
                delete info.el._tooltip;
              }
            });
            
            document.body.appendChild(tooltip);
            
            const rect = info.el.getBoundingClientRect();
            tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 10}px`;
            tooltip.style.left = `${rect.left + window.scrollX}px`;
            
            info.el._tooltip = tooltip;
          }}
          eventMouseLeave={(info) => {
            info.el._hideTimeout = setTimeout(() => {
              if (info.el._tooltip && !info.el._tooltip.matches(':hover')) {
                info.el._tooltip.remove();
                delete info.el._tooltip;
              }
            }, 100);
          }}
        />
      </div>

      {showModal && (
        <div className="modal-overlay active" onClick={() => closeModalWithAnimation(setShowModal)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'add' ? 'Pridať blokovanie' : 'Upraviť blokovanie'}</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(setShowModal)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {modalError && (
                  <div className="alert alert-error mb-3">✕ {modalError}</div>
                )}

                <div className="form-grid-modern">
                  {/* Názov a Typ blokovania v jednom riadku */}
                  <div className="form-row-2col">
                    <div className={`form-group-floating ${formData.nazov ? 'has-value' : ''} ${fieldErrors.nazov ? 'has-error' : ''}`}>
                      <input
                        type="text"
                        name="nazov"
                        id="nazov"
                        className="form-input-floating"
                        value={formData.nazov}
                        onChange={(e) => setFormData({...formData, nazov: e.target.value})}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="nazov" className="form-label-floating">Názov</label>
                      {fieldErrors.nazov && <span className="field-error">{fieldErrors.nazov}</span>}
                    </div>
                    <div className={`form-group-floating ${formData.typ_blokovania ? 'has-value' : ''}`}>
                      <select
                        name="typ_blokovania"
                        id="typ_blokovania"
                        className="form-input-floating"
                        value={formData.typ_blokovania}
                        onChange={(e) => setFormData({...formData, typ_blokovania: e.target.value})}
                        required
                      >
                        <option value="cely_den">Celý deň</option>
                        <option value="casovy_rozsah">Časový rozsah</option>
                        <option value="viacero_dni">Viacero dní</option>
                        <option value="opakujuce_sa">Opakujúce sa</option>
                      </select>
                      <label htmlFor="typ_blokovania" className="form-label-floating">Typ blokovania</label>
                    </div>
                  </div>

              {formData.typ_blokovania === 'cely_den' && (
                  <div className={`form-group-floating full-width ${formData.zaciatok ? 'has-value' : ''}`}>
                    <input
                      type="date"
                      name="zaciatok_cely_den"
                      id="zaciatok_cely_den"
                      className="form-input-floating"
                      value={formData.zaciatok}
                      onChange={(e) => setFormData({...formData, zaciatok: e.target.value})}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="zaciatok_cely_den" className="form-label-floating">Dátum</label>
                  </div>
              )}

              {formData.typ_blokovania === 'casovy_rozsah' && (
                <div className="blocked-time-range-grid">
                  <div className={`form-group-floating ${formData.zaciatok ? 'has-value' : ''}`}>
                    <input
                      type="date"
                      name="zaciatok_datum"
                      className="form-input-floating"
                      value={formData.zaciatok}
                      onChange={(e) => setFormData({...formData, zaciatok: e.target.value})}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="zaciatok_datum" className="form-label-floating">Dátum</label>
                  </div>
                  
                  <div className="time-range-group">
                    <div className={`form-group-floating ${formData.zaciatok_cas ? 'has-value' : ''}`}>
                      <input
                        type="time"
                        name="zaciatok_cas"
                        className="form-input-floating"
                        value={formData.zaciatok_cas}
                        onChange={(e) => setFormData({...formData, zaciatok_cas: e.target.value})}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="zaciatok_cas" className="form-label-floating">Od</label>
                    </div>
                    
                    <span className="time-separator"></span>
                    
                    <div className={`form-group-floating ${formData.koniec_cas ? 'has-value' : ''}`}>
                      <input
                        type="time"
                        name="koniec_cas"
                        className="form-input-floating"
                        value={formData.koniec_cas}
                        onChange={(e) => setFormData({...formData, koniec_cas: e.target.value})}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="koniec_cas" className="form-label-floating">Do</label>
                    </div>
                  </div>
                </div>
              )}

              {formData.typ_blokovania === 'viacero_dni' && (
                  <div className="form-row-2col">
                    <div className={`form-group-floating ${formData.zaciatok ? 'has-value' : ''}`}>
                      <input
                        type="date"
                        name="zaciatok_viacero"
                        className="form-input-floating"
                        value={formData.zaciatok}
                        onChange={(e) => setFormData({...formData, zaciatok: e.target.value})}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="zaciatok_viacero" className="form-label-floating">Dátum od</label>
                    </div>
                    <div className={`form-group-floating ${formData.koniec ? 'has-value' : ''}`}>
                      <input
                        type="date"
                        name="koniec_viacero"
                        className="form-input-floating"
                        value={formData.koniec}
                        onChange={(e) => setFormData({...formData, koniec: e.target.value})}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="koniec_viacero" className="form-label-floating">Dátum do</label>
                    </div>
                  </div>
              )}

              {formData.typ_blokovania === 'opakujuce_sa' && (
                  <div className="recurrence-section">                    
                    {/* Výber dní */}
                    <div className="form-group-floating full-width">
                      <div className="days-picker">
                        {daysOfWeek.map(day => (
                          <button
                            key={day.code}
                            type="button"
                            className={`day-btn ${(formData.selectedDays || []).includes(day.code) ? 'active' : ''}`}
                            onClick={() => toggleDay(day.code)}
                            title={day.label}
                          >
                            {day.short}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Prepínače - Celý deň a Donekonečna vedľa seba */}
                    <div className="switches-row">
                      <label className="switch-container">
                        <input
                          type="checkbox"
                          checked={formData.cely_den_opakovanie}
                          onChange={(e) => setFormData({...formData, cely_den_opakovanie: e.target.checked})}
                        />
                        <span className="switch-slider"></span>
                        <span className="switch-label">Celý deň</span>
                      </label>
                      
                      <label className="switch-container">
                        <input
                          type="checkbox"
                          checked={formData.donekonecna}
                          onChange={(e) => setFormData({...formData, donekonecna: e.target.checked, opakovat_do: e.target.checked ? '' : formData.opakovat_do})}
                        />
                        <span className="switch-slider"></span>
                        <span className="switch-label">Donekonečna</span>
                      </label>
                    </div>
                    
                    {/* Časový rozsah - zobrazí sa vždy, ale disabled ak je celý deň */}
                    <div className={`time-range-row ${formData.cely_den_opakovanie ? 'time-range-disabled' : ''}`}>
                      <div className={`form-group-floating ${formData.zaciatok_cas ? 'has-value' : ''}`}>
                        <input
                          type="time"
                          name="zaciatok_cas_opak"
                          className="form-input-floating"
                          value={formData.zaciatok_cas}
                          onChange={(e) => setFormData({...formData, zaciatok_cas: e.target.value})}
                          placeholder=" "
                          disabled={formData.cely_den_opakovanie}
                        />
                        <label htmlFor="zaciatok_cas_opak" className="form-label-floating">Čas od</label>
                      </div>
                      
                      <span className="time-separator"></span>
                      
                      <div className={`form-group-floating ${formData.koniec_cas ? 'has-value' : ''}`}>
                        <input
                          type="time"
                          name="koniec_cas_opak"
                          className="form-input-floating"
                          value={formData.koniec_cas}
                          onChange={(e) => setFormData({...formData, koniec_cas: e.target.value})}
                          placeholder=" "
                          disabled={formData.cely_den_opakovanie}
                        />
                        <label htmlFor="koniec_cas_opak" className="form-label-floating">Čas do</label>
                      </div>
                    </div>
                  
                    {/* Opakovať od/do */}
                    <div className={`date-range-row date-range-with-arrow ${formData.donekonecna ? 'date-range-disabled' : ''}`}>
                      <div className={`form-group-floating ${formData.opakovat_od ? 'has-value' : ''}`}>
                        <input
                          type="date"
                          name="opakovat_od"
                          className="form-input-floating"
                          value={formData.opakovat_od}
                          onChange={(e) => setFormData({...formData, opakovat_od: e.target.value})}
                          placeholder=" "
                        />
                        <label htmlFor="opakovat_od" className="form-label-floating">Dátum odㅤㅤ</label>
                      </div>
                      
                      <span className="date-separator"></span>
                      
                      <div className={`form-group-floating ${formData.opakovat_do && !formData.donekonecna ? 'has-value' : ''}`}>
                        <input
                          type="date"
                          name="opakovat_do"
                          className="form-input-floating"
                          value={formData.opakovat_do}
                          onChange={(e) => setFormData({...formData, opakovat_do: e.target.value})}
                          placeholder=" "
                          disabled={formData.donekonecna}
                        />
                        <label htmlFor="opakovat_do" className="form-label-floating">Dátum doㅤㅤ</label>
                      </div>
                    </div>
                    
                    {/* Dynamický náhľad */}
                    {getRecurrenceSummary() && (
                      <div className="recurrence-preview">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                          <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                        </svg>
                        <span>{getRecurrenceSummary()}</span>
                      </div>
                    )}
                  </div>
              )}
                </div>
              </div>

              <div className="modal-footer modal-footer-split">
                {modalMode === 'edit' && (
                  <button
                    type="button"
                    className="btn-ghost-danger"
                    onClick={() => {
                      closeModalWithAnimation(setShowModal);
                      setTimeout(() => handleDeleteBlocked(editingBlocked), 300);
                    }}
                  >
                    Vymazať blokovanie
                  </button>
                )}
                <div className="modal-footer-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => closeModalWithAnimation(setShowModal)}>
                    Späť
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === 'add' ? 'Vytvoriť' : 'Uložiť'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showConfirmModal}
        title={confirmModalTitle}
        message={confirmModalMessage}
        onConfirm={() => {
          if (confirmModalAction) {
            confirmModalAction();
          }
          setShowConfirmModal(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
      />

      {showNotificationModal && (
        <div className="modal-overlay" onClick={() => closeModalWithAnimation(() => setShowNotificationModal(false))}>
          <div className="modal-content modal-notification" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-header-${notificationType}`}>
              <h2>{notificationType === 'success' ? 'Úspech' : 'Chyba'}</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(() => setShowNotificationModal(false))}>×</button>
            </div>
            <div className="modal-body">
              <p className="notification-message">{notificationMessage}</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className={`btn ${notificationType === 'success' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => closeModalWithAnimation(() => setShowNotificationModal(false))}
              >
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlockedTimesPage;
